import { Component, inject, input, viewChild, ChangeDetectionStrategy } from '@angular/core';

import { Observable, Subject, merge } from 'rxjs';

import { toObservable } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { finalize, switchMap } from 'rxjs/operators';
import { AppleCommandProgressService } from '../../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../../shared/services/apple-mdm-service';
import { ConfigurationPayloadComponent } from '../../../../sub-features/apple-profiles/profile-components/configuration-payload/configuration-payload.component';
import { ConfigurationProfileComponent } from '../../../../sub-features/apple-profiles/profile-components/configuration-profile/configuration-profile.component';
import { NotificationsProfileComponent } from '../../../../sub-features/apple-profiles/profile-components/notifications-profile/notifications-profile/notifications-profile.component';
import { CommandSpinnerComponent } from '../../../../utilities/command-spinner/command-spinner.component';
import { ConfigurationProfile } from '../../../../viewmodels/apple/configuration-profile.model';
import { NotificationsProfile } from '../../../../viewmodels/apple/profiles/notifications-profile/notifications-profile.model';
import { CommandProgress } from '../../../../viewmodels/apple/signalr/command-context.model';

@Component({
  selector: 'ml-mdm-apple-create-notifications-profile',
  templateUrl: './apple-create-notifications-profile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommandSpinnerComponent,
    NotificationsProfileComponent,
    ConfigurationPayloadComponent,
    ConfigurationProfileComponent,
    FaIconComponent,
  ],
})
export class AppleCreateNotificationsProfileComponent {
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly mdmService = inject(AppleMdmService);

  private readonly notificationsProfileComponent = viewChild.required(NotificationsProfileComponent);

  private readonly configurationProfileComponent = viewChild.required(ConfigurationProfileComponent);

  public notificationsProfile: NotificationsProfile;

  public readonly udid = input.required<string>();
  private readonly _commandProgress = new Subject<CommandProgress>();
  readonly commandProgress$: Observable<CommandProgress> = toObservable(this.udid).pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'InstallProfile'))
    )
  );

  public configurationProfile = new ConfigurationProfile();
  public showMore = false;

  constructor() {
    this.configurationProfile.payloadIdentifier = 'com.methylium.notifications';
    this.configurationProfile.payloadOrganization = 'Methylium B.V.';
    this.configurationProfile.payloadDescription = 'This profile has pre-configured settings for notifications.';
    this.configurationProfile.payloadDisplayName = 'Notifications';

    this.notificationsProfile = new NotificationsProfile();
    this.notificationsProfile.payloadDescription =
      'Notifications profile configures the device to use the specified Notifications settings.';
    this.notificationsProfile.payloadDisplayName = 'Notifications Profile';
    this.notificationsProfile.payloadIdentifier = 'com.methylium.notifications';
    this.notificationsProfile.payloadOrganization = 'Methylium B.V.';
    this.notificationsProfile.payloadVersion = 1;
  }

  public installProfile(_udid: string) {
    // Try to stop editing on both components
    if (!this.notificationsProfileComponent().doneEditing() || !this.configurationProfileComponent().doneEditing()) {
      return;
    }

    const command: CommandProgress = {
      udid: _udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'InstallProfile',
    };
    this._commandProgress.next(command);

    this.configurationProfile.payloadContent = [this.notificationsProfile];
    this.mdmService
      .installProfile(_udid, this.configurationProfile)
      .pipe(
        finalize(() => {
          return;
        })
      )
      .subscribe();
  }
}
